import React, { useEffect, useState } from "react";
import * as Icon from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import { useMenuConfig } from "../hooks/useMenuConfig";
import { RootState } from "../reduxToolkit/store";
import { MenuItem } from "../types/menu.types";
import "./Sidebar.new.css";

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * New Admin Sidebar Component
 * Features:
 * - Role-based menu rendering from JSON config
 * - Collapsible/expandable menu groups
 * - Active route highlighting
 * - Search functionality
 * - Responsive design
 */
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const userRole = useSelector((state: RootState) => state.auth.userRole) ?? "";
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const location = useLocation();

  // Get menu configuration for current user role
  const { menuItems, roleConfig } = useMenuConfig(userRole);

  // Track expanded menu groups
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Search functionality
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<MenuItem[]>([]);

  /**
   * Get the appropriate icon component from react-bootstrap-icons
   */
  const getIconComponent = (iconName: string): React.ComponentType<any> => {
    return (Icon as any)[iconName] || Icon.Circle;
  };

  /**
   * Toggle expansion state of a menu group
   */
  const toggleGroup = (itemId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedGroups(newExpanded);
  };

  /**
   * Auto-expand parent group containing active route
   */
  useEffect(() => {
    const findAndExpandActiveParent = (
      items: MenuItem[],
      path: string
    ): boolean => {
      for (const item of items) {
        if (item.path === path) {
          return true;
        }
        if (item.children) {
          const found = findAndExpandActiveParent(item.children, path);
          if (found && item.id) {
            setExpandedGroups(
              (prev) => new Set([...Array.from(prev), item.id])
            );
            return true;
          }
        }
      }
      return false;
    };

    findAndExpandActiveParent(menuItems, location.pathname);
  }, [location.pathname, menuItems]);

  /**
   * Handle search
   */
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      // Simple search through menu items
      const results: MenuItem[] = [];
      const searchLower = query.toLowerCase();

      const searchItems = (items: MenuItem[]) => {
        for (const item of items) {
          if (item.title.toLowerCase().includes(searchLower)) {
            results.push(item);
          }
          if (item.children) {
            searchItems(item.children);
          }
        }
      };

      searchItems(menuItems);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  /**
   * Clear search
   */
  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
  };

  /**
   * Render a single menu item (with or without children)
   */
  const renderMenuItem = (
    item: MenuItem,
    level: number = 0
  ): React.ReactNode => {
    const ItemIcon = getIconComponent(item.icon);
    const isExpanded = expandedGroups.has(item.id);
    const hasChildren = item.children && item.children.length > 0;

    // Standalone menu item (no children)
    if (!hasChildren && item.path) {
      const isActive = location.pathname === item.path;
      return (
        <NavLink
          key={item.id}
          to={item.path}
          className={`nav-item ${isActive ? "active" : ""} level-${level}`}
          onClick={() => {
            if (window.innerWidth < 768) {
              onToggle(); // Close sidebar on mobile after clicking
            }
          }}
        >
          <ItemIcon className="nav-item-icon" />
          <span className="nav-item-text">{item.title}</span>
          {item.badge && <span className="nav-item-badge">{item.badge}</span>}
        </NavLink>
      );
    }

    // Menu group with children
    if (hasChildren) {
      const handleGroupClick = () => {
        // If sidebar is collapsed, expand it first
        if (!isOpen) {
          onToggle();
          // Auto-expand the group after sidebar opens
          setTimeout(() => {
            toggleGroup(item.id);
          }, 100);
        } else {
          // If sidebar is open, just toggle the group
          toggleGroup(item.id);
        }
      };

      return (
        <div key={item.id} className={`nav-group level-${level}`}>
          <button
            className={`nav-group-header ${isExpanded ? "expanded" : ""}`}
            onClick={handleGroupClick}
          >
            <div className="nav-group-title">
              <ItemIcon className="nav-group-icon" />
              <span className="nav-group-text">{item.title}</span>
            </div>
            <Icon.ChevronRight
              className={`nav-group-chevron ${isExpanded ? "rotated" : ""}`}
            />
          </button>

          <div className={`nav-group-content ${isExpanded ? "expanded" : ""}`}>
            {item.children!.map((child) => renderMenuItem(child, level + 1))}
          </div>
        </div>
      );
    }

    return null;
  };

  /**
   * Render role badge
   */
  const renderRoleBadge = () => {
    if (!roleConfig) return null;

    const RoleIcon = getIconComponent(roleConfig.icon);
    return (
      <div className="sidebar-user-info">
        <div className="user-role-badge" data-role={userRole}>
          <RoleIcon />
          <span>{roleConfig.displayName}</span>
        </div>
      </div>
    );
  };

  // Don't render sidebar if not authenticated
  if (!isAuthenticated) return null;

  // Don't render if no menu items available for this role
  if (menuItems.length === 0) return null;

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && <div className="sidebar-backdrop" onClick={onToggle} />}

      <aside className={`sidebar ${isOpen ? "sidebar-open" : ""}`}>
        {/* Sidebar Header */}
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <Icon.Grid3x3GapFill className="sidebar-brand-icon" />
            <span className="sidebar-brand-text">Admin Panel</span>
          </div>

          {/* Collapse/Expand Button */}
          <button
            className="sidebar-collapse-btn"
            onClick={onToggle}
            title={isOpen ? "Collapse Sidebar" : "Expand Sidebar"}
            aria-label={isOpen ? "Collapse Sidebar" : "Expand Sidebar"}
          >
            <Icon.ChevronLeft
              className={`collapse-icon ${isOpen ? "" : "collapsed"}`}
            />
          </button>
        </div>

        {/* Search Bar (only visible when expanded) */}
        {isOpen && (
          <div className="sidebar-search">
            <div className="search-input-wrapper">
              <Icon.Search className="search-icon" />
              <input
                type="text"
                placeholder="Search menu..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <button
                  className="search-clear-btn"
                  onClick={clearSearch}
                  aria-label="Clear search"
                >
                  <Icon.X />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Sidebar Content */}
        <div className="sidebar-content">
          <nav className="sidebar-nav">
            {searchQuery && searchResults.length > 0 ? (
              // Show search results
              <div className="search-results">
                <div className="search-results-title">
                  Found {searchResults.length} result(s)
                </div>
                {searchResults.map((item) => {
                  if (item.path) {
                    const ItemIcon = getIconComponent(item.icon);
                    const isActive = location.pathname === item.path;
                    return (
                      <NavLink
                        key={item.id}
                        to={item.path}
                        className={`nav-item ${isActive ? "active" : ""}`}
                        onClick={() => {
                          clearSearch();
                          if (window.innerWidth < 768) {
                            onToggle();
                          }
                        }}
                      >
                        <ItemIcon className="nav-item-icon" />
                        <span className="nav-item-text">{item.title}</span>
                      </NavLink>
                    );
                  }
                  return null;
                })}
              </div>
            ) : searchQuery && searchResults.length === 0 ? (
              // No results found
              <div className="no-search-results">
                <Icon.ExclamationCircle />
                <p>No menu items found</p>
              </div>
            ) : (
              // Show regular menu
              menuItems.map((item) => renderMenuItem(item, 0))
            )}
          </nav>
        </div>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">{renderRoleBadge()}</div>
      </aside>
    </>
  );
};

export default Sidebar;
